/**
 * Notifications on this device: the opt-in card shown after a sign-in, and the
 * switch in the user menu. On an iPhone or iPad outside the installed app the
 * card explains Add to Home Screen instead, because Safari tabs cannot receive
 * Web Push.
 */
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useId, useState } from 'react';
import { ApiError } from '../lib/api.ts';
import { askLater, sendTestPush, shouldAsk, syncPush, usePush, type PushState } from '../lib/push.ts';
import { installApp, useCanInstall } from '../lib/pwa.ts';
import type { Me } from '../lib/session.ts';
import { Icon } from './Icon.tsx';
import { EASE } from './motion.tsx';
import { Button, Switch, useToast } from './ui.tsx';

const failure = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.');

/** Mounted once in the shell: keeps this device's subscription in step, and asks once after sign-in. */
export function PushPrompt({ me }: { me: Me }) {
  const qc = useQueryClient();
  const push = usePush(me.userId);
  const toast = useToast();
  const titleId = useId();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void syncPush(me.userId)
      .catch(() => undefined)
      .then(() => qc.invalidateQueries({ queryKey: ['push', me.userId] }));
    // A moment after landing, not on top of the page as it paints.
    const t = setTimeout(() => setAsking(shouldAsk(me.userId)), 1200);
    return () => clearTimeout(t);
  }, [me.userId, qc]);

  const offer = asking && (push.state === 'off' || push.state === 'install-first');
  const later = () => {
    askLater(me.userId);
    setAsking(false);
  };
  const turnOn = async () => {
    setBusy(true);
    try {
      const state = await push.enable();
      setAsking(false);
      if (state === 'on') toast({ title: 'Notifications are on', body: 'Hearth will notify this device.' });
      else if (state === 'blocked') toast({ title: 'Notifications are blocked', body: 'Allow them for this site in your browser settings, then turn them on from the menu.', tone: 'error' });
    } catch (e) {
      toast({ title: 'Couldn’t turn on notifications', body: failure(e), tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {offer ? (
        <motion.aside
          key={push.state}
          className="push-prompt"
          role="dialog"
          aria-labelledby={titleId}
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          <span className="push-prompt__mark" aria-hidden>
            <Icon name={push.state === 'install-first' ? 'share' : 'bell'} />
          </span>
          {push.state === 'install-first' ? (
            <div className="push-prompt__body">
              <div className="eyebrow">iPhone and iPad</div>
              <h2 id={titleId} className="push-prompt__title">
                Add Hearth to your Home Screen
              </h2>
              <ol className="push-prompt__steps">
                <li>
                  Tap <Icon name="share" width={15} height={15} className="push-prompt__inline" /> Share in the browser’s toolbar.
                </li>
                <li>Choose Add to Home Screen.</li>
                <li>Open Hearth from your Home Screen to check in faster and get notified.</li>
              </ol>
              <div className="row" style={{ ['--gap' as string]: '8px' }}>
                <Button size="sm" variant="primary" onClick={later}>
                  Got it
                </Button>
              </div>
            </div>
          ) : (
            <div className="push-prompt__body">
              <div className="eyebrow">On this device</div>
              <h2 id={titleId} className="push-prompt__title">
                Get notified here?
              </h2>
              <p className="muted">Approvals waiting, decisions on your requests and new payslips. Notifications carry a link, never amounts.</p>
              <div className="row" style={{ ['--gap' as string]: '8px' }}>
                <Button size="sm" variant="primary" icon="bell" loading={busy} onClick={turnOn}>
                  Turn on
                </Button>
                <Button size="sm" variant="ghost" onClick={later} disabled={busy}>
                  Not now
                </Button>
              </div>
            </div>
          )}
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}

const HINT: Partial<Record<PushState, string>> = {
  'install-first': 'Add Hearth to your Home Screen to get notifications.',
  blocked: 'Blocked for this site in your browser settings.',
};

/** The user-menu part: the push switch, a test, and Install where the browser offers it. */
export function DevicePrefs({ me }: { me: Me }) {
  const push = usePush(me.userId);
  const toast = useToast();
  const canInstall = useCanInstall();
  const [busy, setBusy] = useState(false);
  const state = push.state;
  const showSwitch = state && state !== 'unsupported';
  if (!showSwitch && !canInstall) return null;

  const toggle = async (on: boolean) => {
    setBusy(true);
    try {
      if (on) {
        const s = await push.enable();
        if (s === 'blocked') toast({ title: 'Notifications are blocked', body: 'Allow them for this site in your browser settings first.', tone: 'error' });
      } else {
        await push.disable();
        toast({ title: 'Notifications are off', body: 'This device won’t get Hearth notifications.' });
      }
    } catch (e) {
      toast({ title: 'Couldn’t change notifications', body: failure(e), tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    try {
      await sendTestPush();
      toast({ title: 'Test sent', body: 'It should appear on this device in a few seconds.' });
    } catch (e) {
      toast({ title: 'Couldn’t send a test', body: failure(e), tone: 'error' });
    }
  };

  return (
    <div className="device-prefs">
      {showSwitch ? (
        <>
          <Switch showLabel label="Notifications on this device" checked={state === 'on'} disabled={busy || state === 'install-first' || state === 'blocked'} onChange={(v) => void toggle(v)} />
          {HINT[state] ? <p className="device-prefs__hint">{HINT[state]}</p> : null}
        </>
      ) : null}
      {state === 'on' || canInstall ? (
        <div className="row" style={{ ['--gap' as string]: '6px' }}>
        {state === 'on' ? (
          <Button variant="ghost" size="sm" icon="bell" onClick={() => void test()}>
            Send a test
          </Button>
        ) : null}
        {canInstall ? (
          <Button variant="ghost" size="sm" icon="download" onClick={() => void installApp()}>
            Install app
          </Button>
        ) : null}
        </div>
      ) : null}
    </div>
  );
}
